<template>
  <div
    id="freebets-campaign-generator-view"
    :class="`min-h-screen px-4 md:!px-1.5 py-4 transition-all duration-300 ${
      isMenuOpen ? 'md:ml-64' : 'md:ml-20'
    }`"
  >
    <div class="max-w-full mx-auto" v-if="!isLoading">
      <div class="mb-4">
        <h1 class="text-3xl font-extrabold text-slate-800 tracking-tight">
          {{ $route.params.id ? "Update" : "Create" }} Campaign
          <span v-if="$route.params.id" class="text-blue-600">{{
            `#${$route.params.id}` ?? ""
          }}</span>
        </h1>
        <p class="text-slate-500 font-medium">
          Configure your campaign settings and distribution rules.
        </p>
      </div>

      <div class="space-y-8">
        <section
          class="bg-white rounded-3xl p-0 shadow-sm border border-slate-100"
        >
          <div class="px-6 pt-4 pb-0 flex items-center gap-3">
            <i
              class="fa-solid fa-gears text-gray-400 w-10 h-10 bg-gray-300/50 rounded-xl flex items-center justify-center"
            ></i>
            <h2 class="font-semibold text-gray-800">
              General Settings ({{formData.skin_id }})
            </h2>
          </div>

          <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-3">
            <!-- Selector Chat Rain vs Mystery Bet Drop -->
            <div class="col-span-1 md:col-span-2 space-y-2 mb-1">
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-600">
                Distribution Type *
              </label>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  @click="formData.is_mistery = false"
                  :class="[
                    'cursor-pointer p-3.5 rounded-2xl border-2 transition-all flex items-start gap-3 select-none',
                    !formData.is_mistery
                      ? 'border-sky-500 bg-sky-50/60 shadow-sm ring-2 ring-sky-500/20'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  ]"
                >
                  <div :class="['p-2.5 rounded-xl flex items-center justify-center text-lg', !formData.is_mistery ? 'bg-sky-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500']">
                    <i class="fa-solid fa-cloud-showers-heavy"></i>
                  </div>
                  <div>
                    <div class="flex items-center gap-2">
                      <h4 class="font-bold text-slate-800 text-sm">Chat Rain</h4>
                      <span v-if="!formData.is_mistery" class="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">Selected</span>
                    </div>
                    <p class="text-xs text-slate-500 mt-1">
                      Promozione distribuita nella chat pubblica della community con pulsante "Claim".
                    </p>
                  </div>
                </div>

                <div
                  @click="formData.is_mistery = true"
                  :class="[
                    'cursor-pointer p-3.5 rounded-2xl border-2 transition-all flex items-start gap-3 select-none',
                    formData.is_mistery
                      ? 'border-purple-500 bg-purple-50/60 shadow-sm ring-2 ring-purple-500/20'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  ]"
                >
                  <div :class="['p-2.5 rounded-xl flex items-center justify-center text-lg', formData.is_mistery ? 'bg-purple-600 text-white shadow-sm' : 'bg-slate-100 text-slate-500']">
                    <i class="fa-solid fa-gift"></i>
                  </div>
                  <div>
                    <div class="flex items-center gap-2">
                      <h4 class="font-bold text-slate-800 text-sm">Mystery Bet Drop</h4>
                      <span v-if="formData.is_mistery" class="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Selected</span>
                    </div>
                    <p class="text-xs text-slate-500 mt-1">
                      Regalo randomico assegnato a sorpresa ai player che scommettono live dopo la bet.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.title") || "Title" }}*</label
              >
              <small class="block text-xs text-gray-400"
                >Campaign identifier</small
              >
              <input
                v-model="formData.title"
                type="text"
                :class="[
                  validationErrors.title
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-100 focus:border-blue-500',
                ]"
                class="w-full px-4 py-2.5 bg-white border rounded-lg outline-none transition-all custom-input"
                placeholder="Enter campaign title"
              />
            </div>

            <div
              v-if="Object.keys(gameChoose).length > 0"
              class="space-y-2 relative"
              v-click-outside="() => closeDropdown('game')"
            >
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"> 
                  Choose Games*</label>
                  <p class="text-xs text-slate-400">
                   Select the games for which you want to activate free bets. If you don't select any games, you can activate them across the entire provider
                 </p>

              <div @click="toggleDropdown('game')"
                    class="custom-input flex items-center justify-between cursor-pointer bg-white min-h-[42px] flex-wrap gap-2 p-2"
                    :class="{
                      'border-emerald-500 ring-2 ring-emerald-500/10': activeDropdown === 'game',
                      'border-red-500': validationErrors.game_id,
                    }">
                    <div v-if="formData.games && formData.games.length > 0" class="flex flex-wrap gap-2">
                      <span v-for="id in formData.games" :key="id"
                        class="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md text-sm font-medium border border-emerald-200">
                        {{ gameChoose[id] }}
                        <button type="button" @click.stop="removeGame(id)"
                          class="hover:text-emerald-900 ml-1 transition-colors">
                          <i class="fa-solid fa-xmark text-xs"></i>
                        </button>
                      </span>
                    </div>

                    <span v-else class="text-slate-400 italic">Select games...</span>

                    <i class="fa-solid fa-chevron-down text-slate-300 transition-transform ml-auto"
                      :class="{ 'rotate-180': activeDropdown === 'game' }"></i>
                  </div>

              <div
                v-if="activeDropdown === 'game'"
                class="absolute dark:bg-slate-800 z-[100] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-150"
              >
                <div class="p-2 border-b border-slate-100 bg-slate-50">
                  <input
                    v-model="searchQueries.game"
                    type="text"
                    class="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 custom-input"
                    placeholder="Search game..."
                  />
                </div>

                <ul class="max-h-48 overflow-y-auto custom-scrollbar">
                  <li
                    v-for="(label, value) in filteredGames"
                    :key="value"
                    @click="selectGame(value)"
                    class="px-3 py-2.5 hover:bg-blue-50 cursor-pointer flex items-center justify-between text-xs transition-colors group"
                    :class="{ 'bg-blue-50/50': formData.game_id === value }"
                  >
                    <div class="flex flex-col">
                      <span
                        class="font-bold uppercase transition-colors"
                        :class="
                          formData.game_id === value
                            ? 'text-blue-600'
                            : 'text-slate-700'
                        "
                      >
                        {{ label }}
                      </span>
                      <span class="text-slate-400 text-[10px]">{{
                        value
                      }}</span>
                    </div>

                    <div class="flex items-center">
                      <i
                        v-if="formData.game_id === value"
                        class="fa-solid fa-check text-blue-500 animate-in zoom-in duration-200"
                      ></i>
                    </div>
                  </li>

                  <li
                    v-if="Object.keys(filteredGames).length === 0"
                    class="px-3 py-4 text-center text-slate-400 text-xs"
                  >
                    No games found
                  </li>
                </ul>
              </div>
            </div>

            <div class="md:col-span-2 space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.description") || "Description" }}*</label
              >
              <small class="block text-xs text-gray-400"
                >Internal description</small
              >
              <textarea
                v-model="formData.description"
                rows="3"
                :class="[
                  validationErrors.description
                    ? 'border-red-500'
                    : 'border-gray-300',
                ]"
                class="w-full custom-input px-4 py-2.5 bg-white border rounded-lg outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all"
                placeholder="Enter campaign description"
              ></textarea>
            </div>

            <div class="md:col-span-2 space-y-2">
              <label class="block text-sm font-semibold text-gray-700">{{
                $t("rain.note") || "Note"
              }}</label>
              <small class="block text-xs text-gray-400">Internal note</small>
              <textarea
                v-model="formData.note"
                rows="2"
                :class="[
                  validationErrors.note ? 'border-red-500' : 'border-gray-300',
                ]"
                class="w-full custom-input px-4 py-2.5 bg-white border rounded-lg outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all"
                placeholder="Internal notes"
              ></textarea>
            </div>
          </div>
        </section>

        <section
          class="bg-white rounded-3xl p-0 shadow-sm border border-slate-100"
        >
          <div class="px-6 pt-4 pb-0 flex items-center gap-3">
            <i
              class="fa-solid fa-timeline text-blue-400 w-10 h-10 bg-blue-300/50 rounded-xl flex items-center justify-center"
            ></i>
            <h2 class="font-semibold text-gray-800">
              {{ $t("rain.date_time_settings") || "Date & Time Settings" }}
            </h2>
          </div>
          <div class="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.start_date") || "Start Date" }}*</label
              >
              <small class="block text-xs text-gray-400">Start date</small>
              <flat-pickr
                v-model="formData.start_date"
                :config="{
                  enableTime: false,
                  dateFormat: 'd/m/Y',
                  minDate: minDateStart(),
                  maxDate: formData.end_date,
                }"
                class="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 custom-input"
              />
            </div>
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.end_date") || "End Date" }}*</label
              >
              <small class="block text-xs text-gray-400">End date</small>
              <flat-pickr
                v-model="formData.end_date"
                :config="{
                  enableTime: false,
                  dateFormat: 'd/m/Y',
                  minDate: formData.start_date,
                }"
                class="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 custom-input"
              />
            </div>
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.start_time") || "Start Time" }}*</label
              >
              <small class="block text-xs text-gray-400">Hour (HH)</small>
              <input
                v-model="formData.start_time"
                type="number"
                min="0"
                max="22"
                @input="validateHourInput('start_time')"
                class="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 custom-input"
                placeholder="0"
              />
            </div>
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.end_time") || "End Time" }}*</label
              >
              <small class="block text-xs text-gray-400">Hour (HH)</small>
              <input
                v-model="formData.end_time"
                type="number"
                min="1"
                max="23"
                @input="validateHourInput('end_time')"
                class="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 custom-input"
                placeholder="23"
              />
            </div>
          </div>
        </section>

        <section
          class="bg-white rounded-3xl p-0 shadow-sm border border-slate-100"
        >
          <div class="px-6 pt-4 pb-0 flex items-center gap-3">
            <i
              class="fa-solid fa-bolt text-yellow-500 w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center"
            ></i>
            <h2 class="font-semibold text-gray-800">Configuration</h2>
          </div>
          <div
            class="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1"
          >
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.max_players") || "Max Players" }}*</label
              >
              <small class="block text-xs text-gray-400 leading-tight"
                >Max freebets per rain</small
              >
              <input
                v-model.number="formData.max_players"
                type="number"
                @input="handleNumericInput($event, 'max_players')"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 custom-input"
                placeholder="10"
              />
            </div>
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.count_day_freebet") || "Daily Freebets" }}*</label
              >
              <small class="block text-xs text-gray-400 leading-tight"
                >Total freebets per day</small
              >
              <input
                v-model.number="formData.count_day_freebet"
                type="number"
                @input="handleNumericInput($event, 'count_day_freebet')"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 custom-input"
                placeholder="10"
              />
            </div>
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.quantity") || "Quantity" }}*</label
              >
              <small class="block text-xs text-gray-400 leading-tight"
                >Total campaign freebets</small
              >
              <input
                v-model.number="formData.quantity"
                type="number"
                @input="handleNumericInput($event, 'quantity')"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 custom-input"
                placeholder="1"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.duration") || "Duration (min)" }}*</label
              >
              <small class="block text-xs text-gray-400 leading-tight"
                >Single rain duration</small
              >
              <input
                v-model.number="formData.duration"
                type="number"
                @input="handleNumericInput($event, 'duration')"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 custom-input"
                placeholder="30"
              />
            </div>
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.deadline") || "Deadline (min)" }}*</label
              >
              <small class="block text-xs text-gray-400 leading-tight"
                >Time to use activated bet</small
              >
              <input
                v-model.number="formData.deadline"
                type="number"
                @input="handleNumericInput($event, 'deadline')"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 custom-input"
                placeholder="5"
              />
            </div>
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{
                  $t("rain.claim_limitation_period") || "Claim Period"
                }}*</label
              >
              <small class="block text-xs text-gray-400 leading-tight"
                >Rains per period</small
              >
              <input
                v-model.number="formData.claim_limitation_period"
                type="number"
                @input="handleNumericInput($event, 'claim_limitation_period')"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 custom-input"
                placeholder="1"
              />
            </div>
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700"
                >{{
                  $t("rain.claim_limitation_day") || "Claim Limit/Day"
                }}*</label
              >
              <small class="block text-xs text-gray-400 leading-tight"
                >Rains per day</small
              >
              <input
                v-model.number="formData.claim_limitation_daily"
                type="number"
                @input="handleNumericInput($event, 'claim_limitation_daily')"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 custom-input"
                placeholder="1"
              />
            </div>

            <div
              class="justify-between p-0 justify-between flex items-start gap-1 flex-col"
            >
              <label class="block text-sm font-semibold text-gray-700"
                >{{ $t("rain.is_enabled") || "Enabled" }}*</label
              >
              <div class="flex items-center h-auto pt-2">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="formData.is_enabled"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"
                  ></div>
                  <span class="ml-3 text-sm font-medium text-gray-600">
                    {{
                      formData.is_enabled
                        ? $t("detail.is_active") || "Active"
                        : $t("detail.is_inactive") || "Inactive"
                    }}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </section>

        <section
          class="bg-white rounded-3xl p-0 shadow-sm border border-slate-100"
        >
          <div
            class="flex items-center justify-between mb-2.5 flex-wrap px-6 pt-4"
          >
            <div class="flex items-center gap-1.5 min-w-full md:min-w-fit">
              <div
                class="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center"
              >
                <i class="fa-solid fa-dollar-sign text-amber-600"></i>
              </div>
              <h2 class="font-semibold text-slate-800">Currency Settings</h2>
            </div>
            <button
              @click="changeCurrency('add')"
              class="flex ms-auto mt-2 md:mt-0 items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl font-semibold text-sm hover:bg-emerald-100 transition-all"
            >
              <i class="fa-solid fa-plus"></i> Add Currency
            </button>
          </div>

          <div class="p-4 md:p-6">
            <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div
                v-for="(curr, index) in formData.currencies"
                :key="index"
                class="relative py-2 px-3 bg-slate-50 rounded-2xl border border-slate-200 group transition-all hover:border-emerald-200 hover:bg-white"
              >
                <button
                  v-if="formData.currencies.length > 1"
                  @click="changeCurrency('remove', index)"
                  class="absolute -top-2 -right-2 w-8 h-8 bg-white border border-rose-100 text-rose-500 rounded-full shadow-sm hover:bg-rose-500 transition-all flex items-center justify-center z-10"
                >
                  <i class="fa-solid fa-times text-xs"></i>
                </button>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-1">
                  <div
                    class="space-y-1.5 relative"
                    v-click-outside="() => closeDropdown('curr' + index)"
                  >
                    <label
                      class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                    >
                      {{ $t("rain.currency_code") || "Currency" }}
                    </label>
                    <div
                      @click="toggleDropdown('curr' + index)"
                      class="w-full px-3 py-2.5 font-semibold rounded-lg border border-slate-200 bg-white flex items-center justify-between text-sm cursor-pointer hover:border-emerald-300 transition-colors"
                    >
                      <span class="text-slate-700">{{
                        curr.currency_code || "Select..."
                      }}</span>
                      <i
                        class="fa-solid fa-angle-down text-slate-300 transition-transform"
                        :class="{
                          'rotate-180': activeDropdown === 'curr' + index,
                        }"
                      ></i>
                    </div>

                    <div
                      v-if="activeDropdown === 'curr' + index"
                      class="absolute dark:bg-slate-800 z-[90] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-150"
                    >
                      <div class="p-2 border-b border-slate-100 bg-slate-50">
                        <input
                          v-model="searchQueries.currency"
                          type="text"
                          class="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 custom-input"
                          placeholder="Search currency..."
                        />
                      </div>
                      <ul class="max-h-48 overflow-y-auto custom-scrollbar">
                        <li
                          v-for="opt in filteredCurrencies"
                          :key="opt.code"
                          @click="selectCurrency(index, opt.code)"
                          class="px-3 py-2.5 hover:bg-emerald-50 cursor-pointer flex items-center justify-between text-xs transition-colors"
                        >
                          <span class="font-bold text-slate-700">{{
                            opt.code
                          }}</span>
                          <span class="text-slate-400 text-[10px]">{{
                            opt.name
                          }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div class="space-y-1.5">
                    <label
                      class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                    >
                      {{ $t("rain.amount") || "Amount" }}
                    </label>
                    <input
                      v-model="curr.amount"
                      type="text"
                      @change="calculateBudget"
                      @input="handleNumericInput($event, index, 'amount')"
                      class="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 transition-all custom-input"
                      placeholder="10.00"
                    />
                  </div>

                  <div class="space-y-1.5">
                    <label
                      class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                    >
                      {{ $t("rain.min_balance") || "Min Balance" }}
                    </label>
                    <input
                      v-model="curr.min_balance"
                      type="text"
                      @input="handleNumericInput($event, index, 'min_balance')"
                      class="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 outline-none custom-input focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 transition-all"
                      placeholder="0.00"
                    />
                  </div>
 
                </div>

                <div class="mt-2 flex flex-wrap gap-3">
                  <div
                    class="flex-1 min-w-[140px] p-3 bg-white rounded-xl border border-slate-100 flex flex-col"
                  >
                    <span
                      class="text-[9px] font-black text-slate-400 uppercase tracking-tighter"
                      >{{ $t("rain.day_budget") }}</span
                    >
                    <span class="text-sm font-bold text-slate-600">
                      {{ formatCurrency(curr.day_budget, curr.currency_code) }}
                    </span>
                  </div>
                  <div
                    class="flex-1 min-w-[140px] p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 flex flex-col"
                  >
                    <span
                      class="text-[9px] font-black text-emerald-500 uppercase tracking-tighter"
                      >{{ $t("rain.total_budget") }}</span
                    >
                    <span class="text-sm font-extrabold text-emerald-700">
                      {{
                        formatCurrency(curr.total_budget, curr.currency_code)
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div
          v-if="!formData.start_date || !formData.end_date"
          class="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl"
        >
          <i class="fa-solid fa-circle-exclamation text-amber-500 mt-1"></i>
          <span class="text-sm text-amber-800 leading-relaxed font-medium">
            {{
              $t("rain.alert_to_calculate_budget") ||
              "In order to correctly calculate the Daily Budget and the Total Budget, it is necessary to set a valid date range."
            }}
          </span>
        </div>

        <div
          class="flex items-center justify-end pt-6 border-t border-gray-200 gap-2"
        >
          <button
            @click="goBack"
            class="px-6 py-2.5 h-auto w-fit rounded-xl bg-slate-200 text-slate-700 font-semibold hover:bg-slate-300 transition-all active:scale-95"
          >
            {{ $t("util.back") }}
          </button>

          <button
            @click="handleSubmit"
            :disabled="Object.keys(validationErrors).length > 0"
            class="text-teal-900 bg-teal-100 inline-flex items-center gap-2 rounded-xl px-4 py-2 transition-colors duration-150 active:scale-95 border border-teal-200"
          >
            <span>Create</span>
            <i class="fa-solid fa-cloud-bolt text-black"></i>
          </button>
        </div>
      </div>
    </div>

    <div
      v-else
      class="flex flex-col items-center justify-center min-h-[400px] space-y-4"
    >
      <Loader />
      <p class="text-gray-400 animate-pulse font-medium">
        Loading campaign data...
      </p>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { format } from "date-fns";
import { mapGetters } from "vuex/dist/vuex.cjs.js";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { postFetch, getFetch, putFetch } from "@/api/axiosClient";
import { parse } from "date-fns";
import Loader from "./Loader.vue";
import store from "@/store/index";

const clickOutside = {
  beforeMount(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted(el) {
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};
export default {
  name: "RainCampaignGenerator",
  components: {
    FlatPickr,
    Loader,
  },
  directives: { "click-outside": clickOutside },
  data() {
    return {
      activeDropdown: null,
      searchQueries: { game: "", currency: "" },
      isLoading: false,
      gameChoose: {},
      validationErrors: {},
      formData: {
        title: "",
        description: "",
        note: "",
        type: "all",
        mode: "rain",
        skin_id: null,
        start_date: null,
        end_date: null,
        start_time: 0,
        end_time: 23,
        max_players: 10,
        count_day_freebet: 10,
        currencies: [
          {
            currency_code: "EUR",
            amount: 10,
            min_balance: 0,
            min_wager: 0,
            day_budget: 0,
            total_budget: 0,
          },
        ],
        claim_limitation_period: 1,
        claim_limitation_daily: 1,
        min_cashout: 1.1,
        duration: 30,
        deadline: 5,
        mode: "rain",
        is_enabled: true,
        games: [],
        quantity: 1,
        min_wager_time: 0,
        is_mistery: false,
      },
      typeOptions: {
        all: "All",
        cashout_1: "Cashout 1",
        cashout_2: "Cashout 2",
        oddeven: "Odd/Even",
        ranges: "Ranges",
        cashout_1_cashout_2: "Cashout 1 + Cashout 2",
        cashout_1_oddeven: "Cashout 1 + Odd/Even",
        cashout_1_ranges: "Cashout 1 + Ranges",
        cashout_2_oddeven: "Cashout 2 + Odd/Even",
        cashout_2_ranges: "Cashout 2 + Ranges",
        oddeven_ranges: "Odd/Even + Ranges",
        cashout_1_cashout_2_oddeven: "Cashout 1 + Cashout 2 + Odd/Even",
        cashout_1_cashout_2_ranges: "Cashout 1 + Cashout 2 + Ranges",
        cashout_1_oddeven_ranges: "Cashout 1 + Odd/Even + Ranges",
        cashout_2_oddeven_ranges: "Cashout 2 + Odd/Even + Ranges",
      },
      gameIDOptions: {
        augustusburger: "Augustus Burger",
        candydreams: "Candy Dreams",
        deeptreasure: "Deep Treasure",
        dragonstale: "Dragons'Tale",
        egyptianbook: "Egyptian Book",
        fruityhills: "Fruity Hills",
        goldenpot: "Golden Pot",
        jokearparty: "Joker Party",
        jungleglade: "Jungle Glade",
        revolverduels: "Revolver Duels",
        riosambaspin: "Rio Samba Spin",
        slotbar90: "Slot Bar 90'",
        straighttojail: "Straight To Jail",
        tigerblessing: "Tiger Blessing",
        vampirecastle: "Vampire Castle",
        zeuswrath: "Zeus' Wrath",
      },
      selectedDatabase: "theearcasino",
      currenciesOpt: [],
    };
  },
  watch: {
    "formData.start_date": "calculateBudget",
    "formData.end_date": "calculateBudget",
    "formData.max_players": "calculateBudget",
    "formData.count_day_freebet": "calculateBudget",
    "formData.quantity": "calculateBudget",
  },
  computed: {
    ...mapGetters(["isMenuOpen", "allowedSkins", "allowedGamesFreebet"]),
    filteredGames() {
      const q = this.searchQueries.game.toLowerCase();
      if (!q) return this.gameChoose;
      return Object.fromEntries(
        Object.entries(this.gameChoose).filter(([_, label]) =>
          label.toLowerCase().includes(q),
        ),
      );
    },
    filteredCurrencies() {
      const q = (this.searchQueries.currency || "").toLowerCase();

      const selectedCodes = (this.formData?.currencies || [])
        .map((c) => c?.currency_code)
        .filter((code) => code);

      return this.currenciesOpt.filter((opt) => {
        if (!opt?.code) return false;

        const code = opt.code.toLowerCase();
        const name = (opt.name || "").toLowerCase();

        const matchesSearch = !q || code.includes(q) || name.includes(q);
        const isNotSelected = !selectedCodes.includes(opt.code);

        return matchesSearch && isNotSelected;
      });
    },
  },
  methods: {
    removeGame(id) {
      this.formData.games = this.formData.games.filter(gameId => gameId !== id);
    },
    async fetchGames() {
      try {
        const res = await getFetch(`/games`);
        const games = Array.isArray(res) ? res : res?.data || [];
        this.gameChoose = Object.fromEntries(
          games.filter((g) => g.is_enabled).map((g) => [g.game_id, g.name]),
        );
 
      } catch (e) {
        console.error("Fetch games error", e);
        this.gameChoose = {};
      }
    },
    toggleDropdown(key) {
      if (this.activeDropdown === key) {
        this.activeDropdown = null;
      } else {
        this.activeDropdown = key;
        if (key === "game") {
          this.searchQueries.game = "";
          this.$nextTick(() => this.$refs.gameSearchInput?.focus());
        } else {
          this.searchQueries.currency = "";
        }
      }
    },
    closeDropdown(key) {
      if (this.activeDropdown === key) this.activeDropdown = null;
    },
    selectGame(id) {
      if (!Array.isArray(this.formData.games)) {
        this.formData.games = [];
      }
      if (!this.formData.games.includes(id)) {
        this.formData.games.push(id);
      } else {
        this.removeGame(id);
      }
    },
    selectCurrency(index, code) {
      this.formData.currencies[index].currency_code = code;
      this.activeDropdown = null;
    },
    changeCurrency(type, index = null) {
      if (type === "add") {
        const usedCodes = this.formData.currencies.map((c) => c.currency_code);

        const firstAvailable = this.currenciesOpt.find(
          (c) => !usedCodes.includes(c.code),
        );

        if (!firstAvailable) {
          return Swal.fire({
            title: "Limit Reached",
            text: "All available currencies have already been added.",
            icon: "info",
          });
        }

        this.formData.currencies.push({
          currency_code: firstAvailable.code,
          amount: 10,
          min_balance: 0,
          min_wager: 0,
          day_budget: 0,
          total_budget: 0,
        });
        this.$nextTick(() => {
          this.calculateBudget();
        });
      } else if (
        type === "remove" &&
        index !== null &&
        this.formData.currencies.length > 1
      ) {
        this.formData.currencies.splice(index, 1);
        this.calculateBudget();
      }
    },
    parseDate(input) {
      let [day, month, year] = input.split("/");
      return new Date(`${year}-${month}-${day}`);
    },
    minDateStart() {
      let today = new Date();
      let startDate = this.formData.start_date
        ? this.parseDate(this.formData.start_date)
        : null;
      return startDate && startDate > today
        ? new Date()
        : this.formData.start_date;
    },
    setRequiredFieldHighlight(val) {
      const requiredFields = [
        "title",
        "description",
        "type",
        "skin_id",
        "start_date",
        "end_date",
        "start_time",
        "end_time",
        "max_players",
        "count_day_freebet",
        "currencies",
        "claim_limitation_period",
        "claim_limitation_daily",
        "min_cashout",
        "duration",
        "deadline",
        "mode",
        "is_enabled",
        "quantity",
        "min_wager_time",
      ];

      let hasMissingField = false;

      requiredFields.forEach((field) => {
        const value = this.formData[field];
        const isEmpty =
          value === "" ||
          value === " " ||
          value === null ||
          value === undefined;

        if (isEmpty) {
          this.validationErrors[field] = val;
          hasMissingField = true;
        }
      });

      if (hasMissingField && val) {
        Swal.fire({
          title: this.$t("rain.error_title") ?? "Error!",
          text:
            this.$t("rain.error_occurred_while_creating_rain") ??
            "An error occurred while creating the Rain Campaign. Check if you have entered all the fields.",
          icon: "error",
        });
      }

      setTimeout(() => {
        this.validationErrors = {};
      }, 4500);
    },
    formatCurrency(amount, currency) {
      if (amount === 0) return `0.00 ${currency}`;
      return `${parseFloat(amount).toFixed(2)} ${currency}`;
    },
    checkValue(key) {
      let value = this.formData[key];

      if (value === null || value === undefined || value === "") {
        this.formData[key] = 0;
      } else if (typeof value !== "number" || isNaN(value)) {
        this.formData[key] = 0;
      } else if (value < 0) {
        this.formData[key] = 0;
      }
    },

    handleNumericInput(event, key) {
      let input = event.target.value;

      if (input === "") {
        input = "0";
      }

      input = input.replace(/,/g, ".");
      input = input.replace(/[^0-9.]/g, "");

      const parts = input.split(".");
      if (parts.length > 2) {
        input = parts[0] + "." + parts.slice(1).join("");
      }

      if (input.includes(".")) {
        const [intPart, decimalPart] = input.split(".");
        input = intPart + "." + decimalPart.slice(0, 2);
      }
      this.formData[key] = input;
    },

    async calculateBudget() {
      let currencies = this.formData.currencies;

      for (let i = 0; i < currencies.length; i++) {
        let daybudget =
          this.formData.max_players *
          this.formData.count_day_freebet *
          this.formData.currencies[i].amount;

        const totalDay = daybudget;
        this.formData.currencies[i].day_budget = Number.isFinite(totalDay)
          ? totalDay
          : 0;
        const formatString = "dd/MM/yyyy";

        if (!this.formData.start_date || !this.formData.end_date) {
          this.formData.currencies[i].total_budget = 0;
          this.formData.currencies[i].day_budget = 0;
          return;
        } else {
          const start = parse(
            this.formData.start_date,
            formatString,
            new Date(),
          );
          const end = parse(this.formData.end_date, formatString, new Date());

          if (isNaN(start) || isNaN(end)) return;

          const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

          const total =
            days *
            this.formData.currencies[i].day_budget *
            this.formData.quantity;

          this.formData.currencies[i].total_budget = Number.isFinite(total)
            ? total
            : 0;
        }
      }
    },

    validateHourInput(key) {
      let value = this.formData[key];

      if (value < 0) {
        this.formData[key] = 0;
      } else if (value > 23) {
        this.formData[key] = 23;
      }

      if (key === "start_time") {
        if (this.formData.start_time === this.formData.end_time) {
          if (this.formData.start_time === 23) {
            this.formData.end_time = 23;
          } else {
            this.formData.end_time = this.formData.start_time + 1;
          }
        }
      }

      if (this.formData.start_time === 0 && this.formData.end_time === 0) {
        this.formData.end_time = 1;
      }

      if (this.formData.start_time === this.formData.end_time) {
        if (this.formData.start_time < 23) {
          this.formData.end_time = this.formData.start_time + 1;
        } else {
          this.formData.end_time = 23;
        }
      }

      if (this.formData.start_time === 23) {
        this.formData.start_time = 22;
      }
    },

    goBack() {
      const lang = this.$route.params.lang || "en";
      const skin = this.$route.params.skinFilt || this.$store.getters.skinStorage;
      this.$router.push({
        name: "Rains",
        params: { lang, ...(skin ? { skinFilt: skin.toString() } : {}) },
      });
    },

    async handleSubmit() {
      this.validationErrors = {};

      this.setRequiredFieldHighlight(true);
 

      const convertDateToISOString = (dateString, type = "start") => {
        const normalizedDateString = dateString.replace(/-/g, "/");
        const [day, month, year] = normalizedDateString.split("/");
        const time = type === "start" ? "T00:00:00.000Z" : "T23:59:59.999Z";
        const date = new Date(`${year}-${month}-${day}${time}`);
        return isNaN(date) ? null : date.toISOString();
      };

      const dataToSend = { ...this.formData };

      const dateFields = [
        { key: "start_date", type: "start" },
        { key: "end_date", type: "end" },
      ];

      for (const { key, type } of dateFields) {
        if (dataToSend[key]) {
          const iso = convertDateToISOString(dataToSend[key], type);
          if (iso) {
            dataToSend[key] = iso;
          } else {
            Swal.fire({
              title: this.$t("rain.error_title") ?? "Error!",
              text: `${
                this.$t("rain.invalid_date_error") ?? "Invalid date format in"
              } ${key.replace("_", " ")}.`,
              icon: "error",
            });
            return;
          }
        }
      }
      ["start_time", "end_time"].forEach((timeField) => {
        if (dataToSend[timeField] !== undefined) {
          const timeValue = String(dataToSend[timeField]);
          if (!timeValue.endsWith(":00")) {
            dataToSend[timeField] = `${timeValue}:00`;
          } else {
            dataToSend[timeField] = timeValue;
          }
        }
      });

      const numericFields = ["min_cashout", "min_balance", "amount"];

      const numericFieldsInt = [
        "max_players",
        "min_wager",
        "min_wager_time",
        "claim_limitation_daily",
        "claim_limitation_period",
        "quantity",
        "count_day_freebet",
        "duration",
        "deadline",
      ];

      numericFields.forEach((field) => {
        if (dataToSend[field] !== undefined) {
          dataToSend[field] = parseFloat(dataToSend[field]).toFixed(2);
        }
      });

      numericFieldsInt.forEach((field) => {
        if (dataToSend[field] !== undefined) {
          dataToSend[field] = parseInt(dataToSend[field]);
        }
      });

      if (this.$route.params.id) {
        try {
          await putFetch(`/campaigns/${this.$route.params.id}`, dataToSend);

          Swal.fire("Success!", "The Rain detail has been updated.").then(
            () => {
              window.location.reload();
            },
          );
        } catch (error) {
          console.error("Error updating Rain detail:", error);
          Swal.fire({
            title: "Error",
            text: "Failed to update the Rain detail.",
            icon: "error",
          });
        }
      } else {
        try {
          const response = await postFetch(`campaigns`, dataToSend);
          const lang = this.$route.params.lang || "en";
          const skin = this.$route.params.skinFilt || this.$store.getters.skinStorage;
          this.$router.push({
            name: "Rains",
            params: { lang, ...(skin ? { skinFilt: skin.toString() } : {}) },
          });
        } catch (error) {
          console.log(error);
        }
      }
    },

    setInitialDate(data = null) {
      if (data == null) {
        let startDate = new Date();
        startDate.setDate(startDate.getDate() + 1);
        let day = String(startDate.getDate()).padStart(2, "0");
        let month = String(startDate.getMonth() + 1).padStart(2, "0");
        let year = startDate.getFullYear();
        startDate = `${day}/${month}/${year}`;
        let endDate = new Date();
        endDate.setDate(endDate.getDate() + 8);
        day = String(endDate.getDate()).padStart(2, "0");
        month = String(endDate.getMonth() + 1).padStart(2, "0");
        year = endDate.getFullYear();
        endDate = `${day}/${month}/${year}`;
        this.formData = {
          ...this.formData,
          start_date: startDate,
          end_date: endDate,
        };
      } else {
        let rawStart = data.start_date;
        let rawEnd = data.end_date;

        const parsedStart = parse(rawStart, "yyyy-MM-dd HH:mm:ss", new Date());
        const parsedEnd = parse(rawEnd, "yyyy-MM-dd HH:mm:ss", new Date());

        const formattedStart = format(parsedStart, "dd/MM/yyyy");
        const formattedEnd = format(parsedEnd, "dd/MM/yyyy");

        this.formData = {
          ...this.formData,
          start_date: formattedStart,
          end_date: formattedEnd,
        };
      }
    },
    async checkSkin() {
      const params = new URLSearchParams(window.location.search);
      const skinFromQuery = params.get("skin_id");
      const paramSkin = this.$route.params.skinFilt;
      let skin = skinFromQuery ?? (paramSkin && paramSkin !== "all" ? paramSkin : this.$store.getters.skinStorage);

      if (skin) {
        this.formData.skin_id = parseInt(skin) || skin;
      }
    },

    async fetchCurrencySuggestions() {
      try {
        let query = "";
        const response = await getFetch(`currencies`, { query });
        this.currenciesOpt = response;
      } catch (err) {
        console.error("Errore durante la ricerca delle valute:", err.message);
        this.currenciesOpt = [];
      }
    },
    async fetchRainDetails() {
      const campaignId_params =
        this.$route.params.id ?? this.$route.query.campaign_id;
      if (!campaignId_params) {
        this.setInitialDate();
        return;
      } else {
        this.isLoading = true;
        try {
          const data = await getFetch(`campaigns/${campaignId_params}`);
          if (!data || Object.keys(data).length === 0) return;

          let start_time = parseInt(data.start_time.split(":")[0], 10); //10:00:
          let end_time = parseInt(data.end_time.split(":")[0], 10);
          this.formData = {
            ...this.formData,
            skin_id: parseInt(data.skin_id),
            games: data.games,
            end_time: data.end_time,
            start_date: null,
            end_date: null,
            description: data.description,
            title: data.title,
            note: data.note,
            type: data.type,
            currencies: data.currencies,
            start_time: start_time,
            end_time: end_time,
            max_players: Number(data.max_players),
            count_day_freebet: Number(data.count_day_freebet),
            amount: Number(data.amount),
            claim_limitation_period: Number(data.claim_limitation_period),
            claim_limitation_daily: Number(data.claim_limitation_daily),
            min_cashout: Number(data.min_cashout),
            duration: Number(data.duration),
            deadline: Number(data.deadline),
            min_balance: Number(data.min_balance),
            quantity: Number(data.quantity),
            min_wager: Number(data?.min_wager ?? 0),
            min_wager_time: Number(data?.min_wager_time ?? 0),
            is_enabled: !!data.is_enabled,
            mode: data.mode,
            is_mistery: !!data.is_mistery,
          };

          this.setInitialDate(data);
        } catch (error) {
          console.error(
            "Errore durante il recupero dei dettagli della campaigns:",
            error.message,
          );
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
  async mounted() {
    import("@/assets/styles/components/swal2ModalDialog.scss");
    if (this.$route.query.is_mistery !== undefined) {
      this.formData.is_mistery =
        this.$route.query.is_mistery === "true" ||
        this.$route.query.is_mistery === true;
    }
    await this.checkSkin();
    await this.calculateBudget().then(() => {
      this.fetchRainDetails();
    });
    await this.fetchCurrencySuggestions();
    this.fetchGames();
  },
  beforeUnmount() {
    const styles = document.querySelectorAll(
      'link[href*="swal2ModalDialog.scss"], style[data-vue-ssr-id*="swal2ModalDialog.scss"]',
    );
    styles.forEach((style) => style.remove());
  },
};
</script>

<style lang="scss" scoped>
.custom-input {
  @apply w-full px-3 py-2 rounded-xl border border-slate-200 focus:!ring-4 focus:!ring-emerald-500/10 focus:!border-emerald-500 transition-all outline-none !text-black;
}

ul::-webkit-scrollbar {
  width: 4px;
}
ul::-webkit-scrollbar-track {
  background: transparent;
}
ul::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}

label {
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

textarea,
select,
input {
  color: black;
}
</style>
